import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Check, MonitorSmartphone, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { PhysicalInterest } from "../components/PhysicalInterest";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { WORLDS, IMG } from "../data/worlds";

const farben = WORLDS[0];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useBuy = () => {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [paymentStatus, setPaymentStatus] = useState(null);
  const buy = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/checkout/intent`, { product_slug: "farben" });
      if (data.status === "redirect" && data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      setMessage(data.message);
      setPaymentStatus(data.payment_status);
      setOpen(true);
    } catch {
      toast.error("Etwas ist schiefgelaufen. Bitte versuche es später erneut.");
    } finally {
      setBusy(false);
    }
  };
  return { buy, busy, open, setOpen, message, paymentStatus };
};

// Post-purchase interest may only appear for confirmed payments ("paid").
// "mock_pending" is allowed for testing until the real checkout is connected.
const showsPostPurchaseInterest = (paymentStatus) => ["paid", "mock_pending"].includes(paymentStatus);

const slideVariants = {
  enter: (d) => ({ x: d > 0 ? "55%" : "-55%", opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (d) => ({ x: d > 0 ? "-55%" : "55%", opacity: 0 }),
};

const arrowStyle = {
  background: "rgba(251,248,243,0.88)",
  backdropFilter: "blur(6px)",
  border: "1px solid var(--line)",
  color: "var(--ink)",
};

export const ProductGallery = ({ testPrefix = "product" }) => {
  const gallery = [
    { label: "Cover", img: farben.cover },
    ...farben.pages.map((p) => ({ label: p.color, img: p.img })),
    { label: "Übersicht", img: IMG.abschluss },
  ];
  const [[active, dir], setState] = useState([0, 0]);

  const go = (next, d) => setState([(next + gallery.length) % gallery.length, d]);
  const paginate = (d) => go(active + d, d);

  const onKeyDown = (e) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      paginate(1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      paginate(-1);
    }
  };

  return (
    <div
      tabIndex={0}
      onKeyDown={onKeyDown}
      role="region"
      aria-label="Produktgalerie – mit Pfeiltasten blätterbar"
      className="outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--terra)] focus-visible:ring-offset-2 rounded-[24px]"
      data-testid={`${testPrefix}-gallery`}
    >
      <div
        className="relative rounded-[24px] overflow-hidden mb-4 bg-white aspect-[3/2] touch-pan-y"
        style={{ boxShadow: "0 22px 54px rgba(44,43,40,0.16)" }}
      >
        <AnimatePresence initial={false} custom={dir}>
          <motion.img
            key={active}
            src={gallery[active].img}
            alt={`Produktansicht ${gallery[active].label}`}
            custom={dir}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.12}
            onDragEnd={(e, info) => {
              if (info.offset.x < -60 || info.velocity.x < -400) paginate(1);
              else if (info.offset.x > 60 || info.velocity.x > 400) paginate(-1);
            }}
            className="absolute inset-0 w-full h-full object-cover cursor-grab active:cursor-grabbing"
            data-testid={`${testPrefix}-gallery-main`}
          />
        </AnimatePresence>

        <button
          onClick={() => paginate(-1)}
          aria-label="Vorherige Seite"
          data-testid={`${testPrefix}-gallery-prev`}
          className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full flex items-center justify-center transition-transform duration-300 hover:scale-110 active:scale-95"
          style={arrowStyle}
        >
          <ChevronLeft size={20} />
        </button>
        <button
          onClick={() => paginate(1)}
          aria-label="Nächste Seite"
          data-testid={`${testPrefix}-gallery-next`}
          className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full flex items-center justify-center transition-transform duration-300 hover:scale-110 active:scale-95"
          style={arrowStyle}
        >
          <ChevronRight size={20} />
        </button>

        <span
          className="absolute bottom-3 right-3 z-10 px-3 py-1.5 rounded-full text-[11px] font-semibold tracking-wide tabular-nums"
          style={arrowStyle}
          data-testid={`${testPrefix}-gallery-indicator`}
        >
          {active + 1} / {gallery.length}
        </span>
      </div>

      <div className="flex gap-2.5 justify-center">
        {gallery.map((g, i) => (
          <button
            key={g.label}
            onClick={() => i !== active && go(i, i > active ? 1 : -1)}
            aria-label={`Ansicht ${g.label}`}
            data-testid={`${testPrefix}-gallery-thumb-${i}`}
            className="w-16 h-11 rounded-xl overflow-hidden border-2 transition-all duration-300"
            style={{ borderColor: active === i ? "var(--terra)" : "transparent", opacity: active === i ? 1 : 0.6 }}
          >
            <img src={g.img} alt="" className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
};

export const ProductPanel = ({ testPrefix = "product" }) => {
  const { buy, busy, open, setOpen, message, paymentStatus } = useBuy();
  return (
    <div>
      <p className="eyebrow-label mb-4">{farben.packageName}</p>
      <h3 className="font-display font-medium tracking-tight text-3xl sm:text-4xl leading-[1.1] mb-2">
        Kleine Welt, große Farben
      </h3>
      <div className="flex items-baseline gap-3 mb-6">
        <span className="font-display text-4xl font-semibold" style={{ color: "var(--terra)" }} data-testid={`${testPrefix}-price`}>
          {farben.price}
        </span>
        <span className="text-xs text-[color:var(--ink-muted)]">Einmalzahlung · inkl. MwSt.</span>
      </div>

      <div className="flex items-center gap-2.5 mb-6 px-4 py-3 rounded-2xl" style={{ background: "#EEF4FC" }}>
        <MonitorSmartphone size={18} style={{ color: "var(--blau)" }} className="shrink-0" />
        <p className="text-[13px] font-medium" style={{ color: "var(--blau)" }}>
          Digitales Produkt – kein physisches Buch enthalten. Sofort verfügbar.
        </p>
      </div>

      <p className="text-[11px] uppercase tracking-[0.22em] font-semibold mb-4" style={{ color: "var(--terra)" }}>
        Das ist enthalten – alle drei Dateien
      </p>
      <div className="space-y-3 mb-6">
        {[
          { title: "VisualMA Mobile Edition", sub: "Für Smartphone & Tablet", meta: "EPUB · Querformat · Seite für Seite wischen" },
          { title: "VisualMA HQ & Print Edition", sub: "Für große Displays & privaten Ausdruck", meta: "Hochauflösende PDF · Zoom · private Druckqualität" },
          { title: "VisualMA Lernsong", sub: "Zum Hören, Mitsingen & Mitmachen", meta: "Vollständiger MP3 Lernsong passend zum Buch" },
        ].map((f) => (
          <div key={f.title} className="card-soft !rounded-2xl p-4 flex items-start gap-3" data-testid={`format-card-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <span className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: "#E4EEE7" }}>
              <Check size={12} style={{ color: "var(--gruen)" }} />
            </span>
            <div>
              <p className="font-semibold text-[15px] leading-snug">
                {f.title} <span className="font-normal text-[color:var(--ink-muted)]">· {f.sub}</span>
              </p>
              <p className="text-[13px] text-[color:var(--ink-muted)] mt-0.5">{f.meta}</p>
            </div>
          </div>
        ))}
      </div>

      <p className="font-display italic text-lg mb-8" data-testid={`${testPrefix}-tagline`}>
        Einmal kaufen. Gemeinsam entdecken.
      </p>

      <div className="flex flex-col sm:flex-row gap-3.5">
        <button className="btn-primary" onClick={buy} disabled={busy} data-testid={`${testPrefix}-buy-button`}>
          Lernpaket für 14,90 € kaufen
        </button>
        <Link to="/lernwelten/farben" className="btn-ghost" data-testid={`${testPrefix}-more-button`}>
          Mehr erfahren
        </Link>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-[24px] max-w-md" style={{ background: "var(--paper-card)" }} data-testid={`${testPrefix}-checkout-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-left">Der Checkout kommt in Kürze</DialogTitle>
            <DialogDescription className="text-left text-[color:var(--ink-muted)]">{message}</DialogDescription>
          </DialogHeader>
          {showsPostPurchaseInterest(paymentStatus) && (
            <div className="border-t border-[color:var(--line)] pt-5 mt-1">
              <p className="font-display text-lg font-medium mb-1.5">VisualMA bald auch zum Anfassen?</p>
              <p className="text-sm text-[color:var(--ink-muted)] mb-5">
                Hättest du zusätzlich Interesse an einer hochwertigen physischen Ausgabe?
              </p>
              <PhysicalInterest source="post_purchase" compact yesLabel="Ja, unbedingt" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const Product = () => (
  <section className="py-24 sm:py-32" data-testid="product-section">
    <div className="max-w-6xl mx-auto px-5 lg:px-8">
      <Reveal className="text-center mb-14">
        <p className="eyebrow-label mb-5">Das Lernpaket</p>
        <h2 className="font-display font-medium tracking-tight text-4xl sm:text-5xl leading-[1.08]">
          Alles für den Start in die<br /><em style={{ color: "var(--terra)" }}>erste Lernwelt.</em>
        </h2>
      </Reveal>
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <Reveal className="max-w-[560px] mx-auto w-full">
          <ProductGallery />
        </Reveal>
        <Reveal delay={0.12}>
          <ProductPanel />
        </Reveal>
      </div>
    </div>
  </section>
);
